// src/hooks/useDxfLoader.js
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import DxfParser from 'dxf-parser';
import { setDxfData, setLoadingMessage } from '../redux/dxfSlice';
import { setLayers } from '../redux/layerSlice';
import { getHatchFromDXF, getLayersFromDXF, getEntityTransparencyMap, getAttribsFromDXF, getOleFramesFromDXF } from '../modules/dxf';

export const useDxfLoader = () => {
  const dispatch = useDispatch();

  const loadDxfFile = useCallback((file, onSuccess, onError) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const parser = new DxfParser();

      try {
        const dxf = parser.parseSync(e.target.result);

        const hatchEntities = getHatchFromDXF(
          e.target.result.split("\n").map((line) => line.trim())
        );
         
        if (Array.isArray(hatchEntities.hatch)) {
          dxf.entities = [...dxf.entities, ...hatchEntities.hatch];
        } else {
          console.warn("HATCH verileri düzgün alınamadı");
        }

        // ✅ Entity'lere transparency ekle (dxf-parser bunu parse etmiyor)
        const dxfLinesForTransparency = e.target.result.split("\n").map((line) => line.trim());
        const transparencyMap = getEntityTransparencyMap(dxfLinesForTransparency);

        // Entity'lere transparency değerlerini ekle (handle'a göre)
        dxf.entities = dxf.entities.map(entity => {
          const handleKey = entity.handle?.toUpperCase();
          if (handleKey && transparencyMap[handleKey] !== undefined) {
            return {
              ...entity,
              transparency: transparencyMap[handleKey]
            };
          }
          return entity;
        });

        // ✅ OLE2FRAME entity'lerini parse et ve ekle (dxf-parser desteklemiyor)
        const oleEntities = getOleFramesFromDXF(
          e.target.result.split("\n").map((line) => line.trim())
        );
        if (oleEntities.length > 0) {
          dxf.entities = [...dxf.entities, ...oleEntities];
        }

        // ✅ ATTRIB'leri parse et ve INSERT entity'lerine bağla
        // dxf-parser ATTRIB'leri parse etmediği için manuel olarak çıkarıyoruz
        const dxfLinesForAttribs = e.target.result.split("\n").map((line) => line.trim());
        const attribsByInsert = getAttribsFromDXF(dxfLinesForAttribs);

        // INSERT entity'lerine attribs dizisini ekle
        dxf.entities = dxf.entities.map(entity => {
          if (entity.type === 'INSERT') {
            const handleKey = entity.handle?.toUpperCase();
            if (handleKey && attribsByInsert[handleKey]) {
              return {
                ...entity,
                attribs: attribsByInsert[handleKey]
              };
            }
          }
          return entity;
        });

        dispatch(setDxfData(dxf));

        // Layer verilerini dxf-parser'dan al
        const parsedLayers = dxf.tables?.layer?.layers;
        let layerArray = Array.isArray(parsedLayers) ? parsedLayers : parsedLayers ? Object.values(parsedLayers) : [];

        // Enhanced layer özellikleri al (locked, onOff - dxf-parser bunları parse etmiyor)
        const dxfLines = e.target.result.split("\n").map((line) => line.trim());
        const enhancedLayers = getLayersFromDXF(dxfLines);

        // dxf-parser layer'larını enhanced özelliklerle zenginleştir
        layerArray = layerArray.map(layer => {
          const enhanced = enhancedLayers[layer.name];
          if (enhanced) {
            return {
              ...layer,
              onOff: enhanced.onOff,      // true = açık, false = kapalı
              locked: enhanced.locked,     // true = kilitli
              frozen: enhanced.frozen,     // true = donmuş (dxf-parser bunu parse ediyor ama kontrol için)
              lineType: enhanced.lineType,
              lineWeight: enhanced.lineWeight,
              transparency: enhanced.transparency ?? 0  // ✅ Transparency: 0 = opak, 255 = tamamen saydam
            };
          }
          return layer;
        });

        dispatch(setLayers(layerArray));
        dispatch(setLoadingMessage('Sahne oluşturuluyor...'));
        if (onSuccess) onSuccess(dxf);
        } catch (err) {
        console.error('DXF parsing error:', err);
        if (onError) onError(err);
      }
    };

    reader.readAsText(file);
  }, [dispatch]);

  return { loadDxfFile };
};